// src/AboutMe.jsx
import React, { useMemo, useRef, useEffect } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { PerspectiveCamera, RenderTexture, Text, OrthographicCamera, Instances, Instance } from '@react-three/drei';
import { Physics, RigidBody, BallCollider, CuboidCollider } from '@react-three/rapier';

// Screen-space projection: sample the portal texture at the fragment's NDC position,
// so every ring acts as a "hole" revealing the same continuous image.
const VERT = /* glsl */`
  varying vec4 vClip;
  void main() {
    vClip = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    gl_Position = vClip;
  }
`;

const FRAG = /* glsl */`
  uniform sampler2D uMap;
  uniform vec3 uTintColor;
  uniform float uTintStrength;
  varying vec4 vClip;
  void main() {
    vec2 uv = vClip.xy / vClip.w * 0.5 + 0.5;
    vec4 base = texture2D(uMap, uv);
    gl_FragColor = vec4(base.rgb + uTintColor * uTintStrength, base.a);
  }
`;

const GRAD_VERT = /* glsl */`
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const GRAD_FRAG = /* glsl */`
  uniform vec3 uTop;
  uniform vec3 uBottom;
  varying vec2 vUv;
  void main() {
    gl_FragColor = vec4(mix(uBottom, uTop, vUv.y), 1.0);
  }
`;

const BODY = `I'm a Ukrainian-American front-end developer\nbased in Florida, focused on building interactive\ndigital experiences with a strong visual identity.\n\nI like exploring new ways for people to navigate\nand interact with the web. My work leans into\nmotion, detail, and unconventional layouts.\n\nOver the past 6+ years, I've honed a development\napproach that balances creativity with technical\nquality — building sites that are fast, engaging,\nand made to explore.`;

// ─────────────────────────────────────────────
// Comet / meteor physics (ported from ContactPage)
// ─────────────────────────────────────────────

function BoundsBox({ halfExtents = [10, 6, 10] }) {
  const [hx, hy, hz] = halfExtents;
  return (
    <RigidBody type="fixed" colliders={false}>
      <CuboidCollider args={[hx, 0.1, hz]} position={[0, -hy, 0]} restitution={1} friction={0.1} />
      <CuboidCollider args={[hx, 0.1, hz]} position={[0,  hy, 0]} restitution={1} friction={0.1} />
      <CuboidCollider args={[0.1, hy, hz]} position={[-hx, 0, 0]} restitution={1} friction={0.1} />
      <CuboidCollider args={[0.1, hy, hz]} position={[ hx, 0, 0]} restitution={1} friction={0.1} />
      <CuboidCollider args={[hx, hy, 0.1]} position={[0, 0, -hz]} restitution={1} friction={0.1} />
      <CuboidCollider args={[hx, hy, 0.1]} position={[0, 0,  hz]} restitution={1} friction={0.1} />
    </RigidBody>
  );
}

function mulberry32(seed) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function generateVoxelPositions(count = 60, seed = 1, jitterAmount = 1) {
  const safeCount = Math.max(1, count | 0);
  const rand = mulberry32(seed);
  const targetR = Math.cbrt((3 * safeCount) / (4 * Math.PI));
  const maxR = Math.ceil(targetR * 1.3);
  const candidates = [];
  const noiseAmount = 2;
  const holeChanceOuter = 0.08;
  const holeChanceInner = 0.02;

  for (let x = -maxR; x <= maxR; x++) {
    for (let y = -maxR; y <= maxR; y++) {
      for (let z = -maxR; z <= maxR; z++) {
        const dist = Math.sqrt(x * x + y * y + z * z);
        const d = dist + (rand() - 0.5) * noiseAmount;
        if (d <= targetR) {
          const norm = dist / targetR;
          const holeChance = norm > 0.7 ? holeChanceOuter : holeChanceInner;
          if (rand() > holeChance) {
            candidates.push([x + (rand() - 0.5) * jitterAmount, y + (rand() - 0.5) * jitterAmount, z + (rand() - 0.5) * jitterAmount]);
          }
        }
      }
    }
  }

  if (candidates.length <= safeCount) return candidates;
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = candidates[i]; candidates[i] = candidates[j]; candidates[j] = tmp;
  }
  return candidates.slice(0, safeCount);
}

function Meteor({ center = [0, 0, 0], voxelSize = 0.6, voxelCount = 60, color = '#c2bee4', roughness = 1, metalness = 0, seed = 1 }) {
  const bodyRef = useRef();
  const positions = useMemo(() => generateVoxelPositions(voxelCount, seed), [voxelCount, seed]);
  const scales    = useMemo(() => positions.map(() => { const s = 0.9 + Math.random() * 0.4; return [s, s, s]; }), [positions.length]);
  const approxRadius = useMemo(() => Math.cbrt((3 * voxelCount) / (4 * Math.PI)) * voxelSize * 1.1, [voxelCount, voxelSize]);

  const initialOffset = useMemo(() => {
    const rand = mulberry32(seed + 111);
    const r = approxRadius * 0.8;
    return [(rand() * 2 - 1) * r, (rand() * 2 - 1) * r, (rand() * 2 - 1) * r];
  }, [seed, approxRadius]);

  const initialLinvel = useMemo(() => {
    const rand = mulberry32(seed + 222);
    const speed = 2;
    return [(rand() * 2 - 1) * speed, (rand() * 2 - 1) * speed, (rand() * 2 - 1) * speed];
  }, [seed]);

  const initialAngvel = useMemo(() => {
    const rand = mulberry32(seed + 333);
    const spin = 1.5;
    return [(rand() * 2 - 1) * spin, (rand() * 2 - 1) * spin, (rand() * 2 - 1) * spin];
  }, [seed]);

  const lastDirRef = useRef(new THREE.Vector3(1, 0, 0));

  useEffect(() => {
    if (!bodyRef.current) return;
    const [vx, vy, vz] = initialLinvel;
    const [ax, ay, az] = initialAngvel;
    bodyRef.current.setLinvel({ x: vx, y: vy, z: vz }, true);
    bodyRef.current.setAngvel({ x: ax, y: ay, z: az }, true);
    const dir = new THREE.Vector3(vx, vy, vz);
    if (dir.lengthSq() > 1e-6) lastDirRef.current.copy(dir.normalize());
  }, [initialLinvel, initialAngvel]);

  useFrame(() => {
    const body = bodyRef.current;
    if (!body) return;
    const v = body.linvel();
    const speedSq = v.x * v.x + v.y * v.y + v.z * v.z;
    if (speedSq < 0.04) {
      const dir = new THREE.Vector3(v.x, v.y, v.z);
      if (dir.lengthSq() > 1e-8) { dir.normalize(); lastDirRef.current.copy(dir); }
      else dir.copy(lastDirRef.current);
      body.applyImpulse({ x: dir.x * 0.6, y: dir.y * 0.6, z: dir.z * 0.6 }, true);
    } else {
      lastDirRef.current.copy(new THREE.Vector3(v.x, v.y, v.z).normalize());
    }
  });

  const [cx, cy, cz] = center;
  const [ox, oy, oz] = initialOffset;

  return (
    <RigidBody ref={bodyRef} colliders={false} position={[cx + ox, cy + oy, cz + oz]}
      mass={1} restitution={1} friction={0.1} linearDamping={0} angularDamping={0}>
      <BallCollider args={[approxRadius]} />
      <Instances limit={positions.length}>
        <boxGeometry args={[voxelSize, voxelSize, voxelSize]} />
        <meshStandardMaterial color={color} roughness={roughness} metalness={metalness} />
        {positions.map((p, i) => (
          <Instance key={i} position={[p[0] * voxelSize, p[1] * voxelSize, p[2] * voxelSize]} scale={scales[i]} />
        ))}
      </Instances>
    </RigidBody>
  );
}

// ─────────────────────────────────────────────
// AnimatedPortalRings
// ─────────────────────────────────────────────
function AnimatedPortalRings({
  count      = 8,
  minRadius  = 0.4,
  maxRadius  = 1.4,
  height     = 2.8,
  tube       = 0.06,
  animSpeed  = 0.25,
  position   = [0, 0, 0],
  portalCamZ = 7.89,
  showText   = true,
}) {
  const timeRef   = useRef(0);
  const meshRefs  = useRef([]);
  const rtMatRef  = useRef(null); // meshBasicMaterial holding the RenderTexture

  const geo = useMemo(
    () => new THREE.TorusGeometry(1, tube, 2, 128),
    [tube]
  );

  const mats = useMemo(() => Array.from({ length: count }, () => new THREE.ShaderMaterial({
    uniforms: {
      uMap:          { value: null },
      uTintColor:    { value: new THREE.Color(1, 1, 1) },
      uTintStrength: { value: 0 },
    },
    vertexShader:   VERT,
    fragmentShader: FRAG,
    side:           THREE.DoubleSide,
  })), [count]);

  const gradMat = useMemo(() => new THREE.ShaderMaterial({
    vertexShader:   GRAD_VERT,
    fragmentShader: GRAD_FRAG,
    uniforms: {
      uTop:    { value: new THREE.Color('#eaf0f3') },
      uBottom: { value: new THREE.Color('#e4e9ed') },
    },
  }), []);

  useFrame((_, dt) => {
    timeRef.current += dt * animSpeed;

    const rtTex = rtMatRef.current?.map ?? null;

    meshRefs.current.forEach((mesh, i) => {
      if (!mesh) return;
      const progress   = (timeRef.current + i / count) % 1;
      const tt         = progress * 2 - 1;      // -1 (bottom) → +1 (top)
      mesh.position.y  = tt * height / 2;
      const baseFactor = Math.sqrt(Math.max(0, 1 - tt * tt));
      const r          = minRadius + (maxRadius - minRadius) * baseFactor;
      mesh.scale.setScalar(r);

      const m = mats[i];
      if (rtTex && m.uniforms.uMap.value !== rtTex) m.uniforms.uMap.value = rtTex;

      // Additive tint driven by vertical position (tt: -1 bottom, +1 top).
      // Edit uTintColor and the strength curve here to taste.
      const tintT = (tt + 1) / 2;                          // 0 (bottom) → 1 (top)
      m.uniforms.uTintColor.value.setRGB(0.4, 0.6, 1.0);   // cool blue tint
      m.uniforms.uTintStrength.value = tintT * 0.25;        // 0 at bottom, 0.25 at top
    });
  });

  return (
    <group position={position}>
      {/*
        Hidden plane: exists only to mount the RenderTexture so it renders each frame.
        Pushed far below the visible scene so it never appears.
      */}
      <mesh position={[0, -9999, 0]}>
        <planeGeometry />
        <meshBasicMaterial ref={rtMatRef} transparent opacity={0}>
          <RenderTexture attach="map" width={1024} height={1024}>
            <PerspectiveCamera
              makeDefault
              position={[0, -1, portalCamZ]}
              fov={35}
              near={0.1}
              far={100}
            />
            <color attach="background" args={['#d4d8db']} />
            {/* Gradient backdrop — large plane behind the text */}
            <mesh position={[0, -1, -3]} material={gradMat}>
              <planeGeometry args={[20, 20]} />
            </mesh>
            {showText && <>
            <Text
              position={[0, 0.2, 0]}
              fontSize={0.35}
              color="#284242"
              anchorX="center"
              anchorY="middle"
              letterSpacing={0.06}
              outlineColor={'white'}
              outlineWidth={0.01}
            >
              About Me
            </Text>
            <Text
              position={[0, -0.1, 0]}
              fontSize={0.11}
              color="#284242"
              anchorX="center"
              anchorY="top"
              maxWidth={2.5}
              textAlign="center"
              lineHeight={1.4}
              outlineColor={'#ffffff'}
              outlineWidth={0.005}
            >
              {BODY}
            </Text>
            </>}
          </RenderTexture>
        </meshBasicMaterial>
      </mesh>

      <group rotation={[Math.PI/6,0,-Math.PI/6]}>
      {Array.from({ length: count }, (_, i) => (
        <mesh
          key={i}
          ref={el => { meshRefs.current[i] = el; }}
          geometry={geo}
          rotation={[Math.PI / 2, 0, 0]}
          material={mats[i]}
        />
      ))}
      </group>
    </group>
  );
}

// ─────────────────────────────────────────────
// AboutMe scene with pointer repulsion
// ─────────────────────────────────────────────
export default function AboutMe({ pointer }) {
  const camRef       = useRef();
  const ringGroupRef = useRef();

  const baseWorldPos = useMemo(() => new THREE.Vector3(0, 4, 4), []);
  const tmpVec       = useMemo(() => new THREE.Vector3(), []);

  const { camera, size } = useThree();

  const camZ = size.width < 600 ? 16 : size.width < 900 ? 13 : 10;

  // useFrame(() => {
  //   if (!ringGroupRef.current) return;

  //   const { width, height } = size;

  //   const ndc     = tmpVec.copy(baseWorldPos).project(camera);
  //   const anchorX = (ndc.x * 0.5 + 0.5) * width;
  //   const anchorY = (-ndc.y * 0.5 + 0.5) * height;

  //   const px = pointer?.x ?? anchorX;
  //   const py = pointer?.y ?? anchorY;

  //   const dx   = anchorX - px;
  //   const dy   = anchorY - py;
  //   const dist = Math.sqrt(dx * dx + dy * dy) || 1;

  //   const maxRadius = 800;
  //   const strength  = 1 - Math.min(dist, maxRadius) / maxRadius;

  //   const intensity3D = { x: 0.9, y: 0.6 };
  //   const targetX = baseWorldPos.x + (dx / dist) * strength * intensity3D.x;
  //   const targetY = baseWorldPos.y - (dy / dist) * strength * intensity3D.y;

  //   const pos = ringGroupRef.current.position;
  //   pos.x = THREE.MathUtils.lerp(pos.x, targetX, 0.08);
  //   pos.y = THREE.MathUtils.lerp(pos.y, targetY, 0.08);
  // });

  return (
    <>
      <PerspectiveCamera
        ref={camRef}
        makeDefault
        position={[0, 4, camZ]}
        fov={45}
        near={0.1}
        far={100}
      />

      <ambientLight intensity={0.5} color="#fff" />
      <directionalLight
        position={[5, 10, 2]}
        intensity={6}
        color="#f2feff"
        castShadow
        shadow-mapSize-width={1024}
        shadow-mapSize-height={1024}
        shadow-camera-near={1}
        shadow-camera-far={40}
        shadow-camera-left={-10}
        shadow-camera-right={10}
        shadow-camera-top={10}
        shadow-camera-bottom={-10}
        shadow-bias={-0.0005}
      />

      <Physics gravity={[0, 0, 0]} timeStep="vary">
        {/* bounds centered on the AboutMe scene, offset by camera Y=4 */}
        <group position={[0, 4, 4]}>
          <BoundsBox halfExtents={[10, 8, 10]} />
        </group>
        {/* static body blocking comets from passing through the ring sphere */}
        <RigidBody type="fixed" colliders={false} position={[0, 4, 4]}>
          <BallCollider args={[2]} />
        </RigidBody>
        <Meteor center={[3,  3, 2]} voxelCount={20} voxelSize={0.7} seed={1} />
        <Meteor center={[3,  3, 2]} voxelCount={26} voxelSize={0.5} seed={2} />
        <Meteor center={[3,  3, 2]} voxelCount={30} voxelSize={0.6} seed={3} />
        <Meteor center={[-3, 5, 1]} voxelCount={20} voxelSize={0.7} seed={4} />
        <Meteor center={[-3, 5, 1]} voxelCount={26} voxelSize={0.5} seed={5} />
        <Meteor center={[-3, 5, 1]} voxelCount={30} voxelSize={0.6} seed={6} />
        <Meteor center={[-3, 5, 1]} voxelCount={24} voxelSize={0.6} seed={7} />
        <Meteor center={[-3, 5, 1]} voxelCount={16} voxelSize={0.7} seed={8} />
        <Meteor center={[-3, 5, 1]} voxelCount={28} voxelSize={0.6} seed={9} />
        <Meteor center={[-3, 5, 1]} voxelCount={34} voxelSize={0.4} seed={10} />
      </Physics>

      <group ref={ringGroupRef} position={[0, 4, 4]}>
        <AnimatedPortalRings
          count={8}
          minRadius={0.2}
          maxRadius={1.8}
          height={3.5}
          tube={0.05}
          animSpeed={0.2}
          portalCamZ={size.width < 600 ? 13 : size.width < 900 ? 11.85 : 7.89}
          showText={size.width >= 768}
        />
        <group position={[0,1,0]}>
        <Text
          position={[0, 0.2, 0]}
          fontSize={0.35}
          color="#8db6e3"
          anchorX="center"
          anchorY="middle"
          letterSpacing={0.06}
          material-depthTest={true}
        >
          About Me
        </Text>

        <Text
          position={[0, -0.1, 0]}
          fontSize={0.11}
          color="#8db6e3"
          anchorX="center"
          anchorY="top"
          maxWidth={2.5}
          textAlign="center"
          lineHeight={1.4}
          material-depthTest={true}
        >
          {BODY}
        </Text>
        </group>
      </group>
    </>
  );
}
